import { Box, Typography } from "@mui/material";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  isSameMonth,
  isToday,
  getDate,
} from "date-fns";
import { AppointmentData } from "hooks/useAppointments";
import { themeColors } from "theme/colorTokens";

export interface AppointmentMonthViewProps {
  currentDate: Date;
  getAppointmentsForDay: (date: Date) => AppointmentData[];
  getColorByCode: (code?: number) => string;
  handleEditAppointment: (appointment: AppointmentData) => void;
  onDateClick?: (date: Date) => void;
}

const AppointmentMonthView = ({
  currentDate,
  getAppointmentsForDay,
  getColorByCode,
  handleEditAppointment,
  onDateClick,
}: AppointmentMonthViewProps) => {
  const monthStart = startOfMonth(currentDate);
  const monthEnd = endOfMonth(currentDate);
  const startDate = startOfWeek(monthStart, { weekStartsOn: 0 }); // Sunday
  const endDate = endOfWeek(monthEnd, { weekStartsOn: 0 }); // Sunday

  const days: Date[] = [];
  let day = startDate;
  while (day <= endDate) {
    days.push(day);
    day = addDays(day, 1);
  }

  const dayHeaders = ["SUN", "MON", "TUE", "WED", "THU", "FRI", "SAT"];

  const handleCellClick = (date: Date) => {
    if (onDateClick) {
      onDateClick(date);
    }
  };

  return (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: themeColors.background.paper,
      }}
    >
      {/* Day Headers */}
      <Box
        sx={{
          display: "flex",
          borderBottom: `1px solid ${themeColors.border.light}`,
          backgroundColor: themeColors.background.paper,
        }}
      >
        {dayHeaders.map((day) => (
          <Box
            key={day}
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "12px 4px",
              borderRight: `1px solid ${themeColors.border.light}`,
              "&:last-child": {
                borderRight: "none",
              },
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontWeight: 600,
                color: themeColors.text.secondary,
                fontSize: "0.75rem",
              }}
            >
              {day}
            </Typography>
          </Box>
        ))}
      </Box>

      {/* Calendar Grid */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "auto",
        }}
      >
        {Array.from({ length: Math.ceil(days.length / 7) }).map(
          (_, weekIndex) => (
            <Box
              key={weekIndex}
              sx={{
                display: "flex",
                flex: 1,
                minHeight: "120px",
                borderBottom: `1px solid ${themeColors.border.light}`,
                "&:last-child": {
                  borderBottom: "none",
                },
              }}
            >
              {days.slice(weekIndex * 7, weekIndex * 7 + 7).map((date) => {
                const isCurrentMonth = isSameMonth(date, currentDate);
                const isCurrentDay = isToday(date);
                const dayAppointments = getAppointmentsForDay(date);
                const isFirstOfMonth = getDate(date) === 1;
                const monthAbbr = format(date, "MMM");

                return (
                  <Box
                    key={date.toISOString()}
                    onClick={() => handleCellClick(date)}
                    sx={{
                      flex: 1,
                      borderRight: `1px solid ${themeColors.border.light}`,
                      padding: "8px 4px",
                      backgroundColor: isCurrentMonth
                        ? themeColors.background.paper
                        : themeColors.background.default,
                      cursor: "pointer",
                      position: "relative",
                      display: "flex",
                      flexDirection: "column",
                      minHeight: "120px",
                      height: "100%",
                      overflow: "hidden",
                      "&:hover": {
                        backgroundColor: themeColors.background.secondary,
                      },
                      "&:last-child": {
                        borderRight: "none",
                      },
                    }}
                  >
                    {/* Date Number */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        marginBottom: "4px",
                        position: "relative",
                        zIndex: 2,
                        flexShrink: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          position: "relative",
                        }}
                      >
                        {/* Current Day Indicator */}
                        {isCurrentDay ? (
                          <Box
                            sx={{
                              width: "28px",
                              height: "28px",
                              borderRadius: "50%",
                              pointerEvents: "none",
                              backgroundColor: themeColors.background.navy,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: themeColors.text.inverse,
                            }}
                          >
                            {getDate(date)}
                          </Box>
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 500,
                              color: isCurrentMonth
                                ? themeColors.text.primary
                                : themeColors.text.secondary,
                              fontSize: "0.875rem",
                              opacity: isCurrentMonth ? 1 : 0.5,
                              paddingLeft: "7px",
                            }}
                          >
                            {getDate(date)}
                          </Typography>
                        )}
                      </Box>
                      {isFirstOfMonth && (
                        <Typography
                          variant="caption"
                          sx={{
                            color: themeColors.text.secondary,
                            fontSize: "0.7rem",
                            fontWeight: 500,
                          }}
                        >
                          {monthAbbr}
                        </Typography>
                      )}
                    </Box>

                    {/* Appointments */}
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "2px",
                        marginTop: "4px",
                        flex: 1,
                        overflowY: "auto",
                        overflowX: "hidden",
                        minHeight: 0,
                        "&::-webkit-scrollbar": {
                          width: "4px",
                        },
                        "&::-webkit-scrollbar-track": {
                          background: "transparent",
                        },
                        "&::-webkit-scrollbar-thumb": {
                          background: themeColors.border.light,
                          borderRadius: "2px",
                          opacity: 0.6,
                        },
                        "&::-webkit-scrollbar-thumb:hover": {
                          opacity: 1,
                          background: themeColors.text.secondary,
                        },
                      }}
                    >
                      {dayAppointments.map((appointment) => (
                        <Box
                          key={appointment.id}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleEditAppointment(appointment);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: "4px",
                            padding: "2px 4px",
                            borderRadius: "4px",
                            cursor: "pointer",
                            flexShrink: 0,
                            "&:hover": {
                              backgroundColor: themeColors.background.secondary,
                            },
                          }}
                        >
                          <Box
                            sx={{
                              width: "8px",
                              height: "8px",
                              borderRadius: "50%",
                              backgroundColor: getColorByCode(
                                appointment.colorCode
                              ),
                              flexShrink: 0,
                            }}
                          />
                          <Typography
                            variant="caption"
                            sx={{
                              color: themeColors.text.primary,
                              fontSize: "0.7rem",
                              fontWeight: 400,
                              whiteSpace: "nowrap",
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              flex: 1,
                            }}
                          >
                            {appointment.startTime
                              ? format(appointment.startTime, "h:mma")
                              : ""}{" "}
                            {appointment.patientName || "Appointment"}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                );
              })}
            </Box>
          )
        )}
      </Box>
    </Box>
  );
};

export default AppointmentMonthView;
